import React from 'react';
import { Box, Paper } from '@mui/material';
import { ESG_CONTENT_SHAPE } from '../../../constants/colors';
import { contentBorders } from '../../../theme/createContentTheme';
import KpiFigure, { type KpiFigureProps } from '../../metrics/KpiFigure';

interface PerformanceHeroProps {
  /** Lead figure (rendered hero size). */
  lead?: KpiFigureProps | null;
  /** Supporting figure (rendered large, in the side column). */
  side?: KpiFigureProps | null;
}

/**
 * Asymmetric hero panel: one editorial lead figure plus a supporting figure,
 * 8/4 on large screens, 7/5 on tablets, stacked with a rule on mobile.
 */
const PerformanceHero: React.FC<PerformanceHeroProps> = ({ lead, side }) => {
  if (!lead && !side) return null;
  const both = Boolean(lead && side);
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: `${ESG_CONTENT_SHAPE.md}px`,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: both
          ? { xs: '1fr', md: 'minmax(0, 7fr) minmax(0, 5fr)', lg: 'minmax(0, 8fr) minmax(0, 4fr)' }
          : '1fr',
      }}
    >
      {lead ? (
        <Box sx={{ p: { xs: 2.5, md: 3.5 }, minWidth: 0 }}>
          <KpiFigure {...lead} size="hero" />
        </Box>
      ) : null}
      {side ? (
        <Box
          sx={(theme) => ({
            p: { xs: 2.5, md: 3.5 },
            minWidth: 0,
            ...(both
              ? {
                  borderTop: { xs: `1px solid ${contentBorders(theme).default}`, md: 'none' },
                  borderInlineStart: { xs: 'none', md: `1px solid ${contentBorders(theme).default}` },
                }
              : {}),
          })}
        >
          <KpiFigure {...side} size={both ? 'lg' : 'hero'} />
        </Box>
      ) : null}
    </Paper>
  );
};

export default PerformanceHero;
